import { ProposalShoppingCart } from '@components/BookingProposal/ProposalShoppingCart';
import { getProposalSummaryDecimals } from '@components/BookingProposal/proposalSummaryDecimals';
import { CheckoutLoginStep } from '@components/Login/CheckoutLoginStep';
import { plural } from '@lingui/core/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { customerRoutes, formatCurrencyAmount } from 'noddi-util';
import { useAuthContext } from 'noddi-web';
import { useProposalCatalogPrices } from '../../../hooks/useProposalCatalogPrices';
import { useTrackProposalStep } from '../../../hooks/useTrackProposalStep';
import {
  useBookingProposalItems,
  useBookingProposalServiceDepartmentId,
  useBookingProposalSlug
} from '../../../stores/bookingProposalStore';
import { getProposalCartTotal } from '../../../utils/proposalCartTotal';

/** Login in front of the home-delivery proposal's confirm page. */
export const BookingProposalLogin = () => {
  const slug = useBookingProposalSlug();
  const { isLoggedIn } = useAuthContext();
  const bookingProposalItems = useBookingProposalItems();
  const serviceDepartmentId = useBookingProposalServiceDepartmentId();
  const { data: proposal } = noddiAsync.useGet({
    type: URLKeys.getBookingProposalBySlug,
    input: { slug }
  });
  const {
    prices: catalogPrices,
    priceByCartKey,
    isPending: isCatalogPricesPending
  } = useProposalCatalogPrices(proposal);
  // A logged-in visitor is redirected on, so only a real login screen counts as a view.
  useTrackProposalStep('login', !isLoggedIn);

  const userGroupId = proposal?.userGroupMember.userGroup.id;
  const total = getProposalCartTotal(bookingProposalItems, priceByCartKey);
  const serviceCount = bookingProposalItems.reduce((sum, item) => sum + item.salesItemsWithQuantity.length, 0);

  return (
    <CheckoutLoginStep
      nextPath={customerRoutes.bookingProposalConfirmation.getPath({ slug })}
      // Same width as every other step of the flow — a narrower shell here makes the page jump between steps.
      className='max-w-screen-md'
      userGroupId={userGroupId}
      opensUserGroupPicker
      // The recap is optional here: a slow or failed proposal must never hold up the login.
      order={
        userGroupId && !isCatalogPricesPending
          ? {
              description: plural(serviceCount, { one: '# service', other: '# services' }),
              // Same decimals rule as the cart below, so the row and its total read alike.
              total: formatCurrencyAmount({ money: { amount: total }, decimals: getProposalSummaryDecimals([total]) }),
              details: (
                <ProposalShoppingCart
                  userGroupId={userGroupId}
                  wrapCarsInCard={false}
                  serviceDepartmentId={serviceDepartmentId}
                  bookingProposalItems={bookingProposalItems}
                  catalogPrices={catalogPrices}
                />
              )
            }
          : undefined
      }
    />
  );
};
